import type { AreaDimensions, OptionsDocument, QuoteArea, QuoteDocument } from 'paintscout'
import type { QuoteItemConsumer, QuoteItemSection, RenderableItem } from '../../../index'
import { generateAreaDescription, getItemSection } from '../../util'
// Import the leaf module, not the calculator barrel: the barrel re-exports submodules that import
// back into builder/quote, and on the reindex path (indexQuote -> getAnalytics -> getItems ->
// trimArea) that circular edge leaves the barrel's sectionTranslate export undefined at call time.
import { sectionTranslate } from '../../../../calculator/util/section-translate'

export function trimArea(args: {
  item: QuoteArea
  quote: QuoteDocument
  options: OptionsDocument
  consumer: QuoteItemConsumer
  section?: QuoteItemSection
  trimBySubstrateSection?: QuoteItemSection
}): RenderableItem {
  const { item, quote, options, consumer, section, trimBySubstrateSection } = args
  const areaSection = getItemSection(item)

  const description = {
    useCustom: !!item.substrate_string.use_custom,
    custom: item.substrate_string.custom,
    default: generateAreaDescription({ quote, options, areaKey: item.key, section: section ? section : 'bid' })
  }

  let hours = item.totals.override_hours ? item.totals.override_hours_value : item.totals.hours
  let prep = item.totals.override_prep ? item.totals.override_prep_value : item.totals.prep

  let price = 0
  let materials = 0

  price = item.totals.override_price ? item.totals.override_price_value : item.totals.price
  materials = item.totals.override_materials ? item.totals.override_materials_value : item.totals.materials
  const overridden =
    item.totals.override_price ||
    item.totals.override_materials ||
    item.totals.override_hours ||
    item.totals.override_prep

  if (
    trimBySubstrateSection == undefined &&
    (section === 'additional' || section === 'pending' || section === 'added-options')
  ) {
    const sectionName = sectionTranslate(section)
    if (areaSection === section) {
      if (item.totals.override_price) {
        price = item.totals.override_price_value
      } else {
        const areaPrice = item.totals.price ? item.totals.price : 0
        const substratesPrice = item.totals[sectionName]?.price ? item.totals[sectionName]?.price : 0
        price = areaPrice + substratesPrice
      }

      if (item.totals.override_materials) {
        materials = item.totals.override_materials_value
      } else {
        const areaMaterials = item.totals.price ? item.totals.materials : 0
        const substratesMaterials = item.totals[sectionName]?.materials ? item.totals[sectionName]?.materials : 0
        materials = areaMaterials + substratesMaterials
      }

      if (item.totals.override_hours) {
        hours = item.totals.override_hours_value
      } else {
        const areaHours = item.totals.hours ? item.totals.hours : 0
        const substratesHours = item.totals[sectionName]?.hours ? item.totals[sectionName]?.hours : 0
        hours = areaHours + substratesHours
      }

      if (item.totals.override_prep) {
        prep = item.totals.override_prep_value
      } else {
        const areaPrep = item.totals.prep ? item.totals.prep : 0
        const substratesPrep = item.totals[sectionName]?.prep ? item.totals[sectionName]?.prep : 0
        prep = areaPrep + substratesPrep
      }
    } else {
      prep = item.totals[sectionName]?.prep
      hours = item.totals[sectionName]?.hours
      price = item.totals[sectionName]?.price
      materials = item.totals[sectionName]?.materials
    }
  } else {
    price = item.totals.override_price ? item.totals.override_price_value : item.totals?.price
    materials = item.totals.override_materials ? item.totals.override_materials_value : item.totals.materials
  }

  if (!price) {
    price = 0
  }
  if (!materials) {
    materials = 0
  }

  const totalHours = hours + prep
  const totalPrice = materials + price

  const name = item.area_label.value
  let dimensionsString = ''
  if (consumer === 'crew') {
    const dimensions = item.dimensions as AreaDimensions
    let dimensionsItems = []

    if (item.area_type?.value === 'room') {
      dimensionsItems = [dimensions.length, dimensions.width, dimensions.height]
    } else {
      dimensionsItems = [dimensions.length, dimensions.height]
    }

    dimensionsString = dimensionsItems
      .filter((item) => item)
      .map((item) => `${item}'`)
      .join('x')

    materials = 0
  }

  return {
    key: item.key,
    type: 'area',
    quoteType: item.int_ext?.value,
    name,
    subtitle: dimensionsString,
    files: item.files,
    images: item.images,
    description,
    value: consumer === 'crew' ? totalHours : totalPrice,
    additionalFields: {
      prep,
      painting: hours,
      price,
      crewNote: item.internal_note,
      clientNote: item.client_note,
      materials: consumer === 'crew' ? materials || [] : materials || 0,
      overridden
    }
  }
}
